import React from 'react';
import PropTypes from 'prop-types';

/**
 * QuestionStatsBadge - Displays question performance statistics
 * 
 * Shows:
 * - Times answered
 * - Success rate
 * - Color-coded performance indicator
 * 
 * Colors:
 * - Green: >80% correct
 * - Yellow: 50-80% correct
 * - Red: <50% correct
 * - Gray: Never answered
 * 
 * @param {Object} props
 * @param {number} props.timesAnswered - Total attempts
 * @param {number} props.timesCorrect - Correct attempts
 * @param {string} props.size - Size variant ('sm', 'md', 'lg')
 */
function QuestionStatsBadge({
  timesAnswered = 0,
  timesCorrect = 0,
  size = 'md'
}) {
  // Calculate success rate
  const successRate = timesAnswered > 0 
    ? Math.round((timesCorrect / timesAnswered) * 100) 
    : 0;
  
  // Determine color based on performance
  let colorClasses;
  if (timesAnswered === 0) {
    colorClasses = 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600';
  } else if (successRate >= 80) {
    colorClasses = 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700';
  } else if (successRate >= 50) {
    colorClasses = 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700';
  } else {
    colorClasses = 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700';
  }
  
  // Size variants
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-1.5 text-base'
  };
  
  // Icon based on performance
  const getIcon = () => {
    if (timesAnswered === 0) {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    } else if (successRate >= 80) {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    } else if (successRate >= 50) {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      );
    } else {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    }
  };
  
  return (
    <div 
      className={`
        inline-flex items-center gap-2 rounded-full border font-medium theme-transition
        ${colorClasses}
        ${sizeClasses[size]}
      `}
      title={timesAnswered > 0 
        ? `${timesCorrect} von ${timesAnswered} richtig (${successRate}%)`
        : 'Noch nicht beantwortet'
      }
    >
      {getIcon()}
      <span>
        {timesAnswered > 0 
          ? `${timesAnswered}x gesehen • ${timesCorrect}x richtig (${successRate}%)`
          : 'Neue Frage'
        }
      </span>
    </div>
  );
}

QuestionStatsBadge.propTypes = {
  timesAnswered: PropTypes.number,
  timesCorrect: PropTypes.number,
  size: PropTypes.oneOf(['sm', 'md', 'lg'])
};

export default QuestionStatsBadge;
